import { CDN_URL } from '../config.js';
import { resolveCreateCommonText } from '../translations.js';
import { getNsfwEnabled } from './userPrefs.js';

const BOUNDED_NSFW_STEMS = new Set(['sex', 'cum', 'anus']);
const REVERSE_NSFW_KEYWORDS = new Set([
  'handjob', 'buttjob', 'hairjob', 'missionary', 'cowgirl_position',
  'doggystyle', 'fellatio', 'irrumatio', 'footjob', 'paizuri', 'naizuri',
  'pull_out', 'nude', 'explicit', 'penis', 'dildo', 'ass_focus', 'ass_grab',
  'moaning', 'crotch', 'pussy'
]);
let cachedKeywords = [];
let cachedExact = [];
let cachedHiddenOutfitIds = [];
let cachedHiddenOutfitNames = [];
let cachedHiddenScenarioTagIds = [];
let cachedHiddenScenarioTags = [];
let loadPromise = null;

const normalizeTag = (value) => String(value || '').toLowerCase().replace(/\s+/g, '_').trim();

const tagList = (values) => (Array.isArray(values) ? values : [])
  .map(normalizeTag)
  .filter(Boolean);

const normalizeOutfitName = (value) => String(value || '')
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const outfitIdList = (values) => (Array.isArray(values) ? values : [])
  .map((id) => Number.parseInt(String(id), 10))
  .filter((id) => Number.isFinite(id));

const parseOutfitId = (outfit) => {
  if (typeof outfit === 'number') return outfit;
  const raw = outfit?.id ?? outfit?.outfit_id ?? outfit?.outfitId;
  const id = Number.parseInt(String(raw), 10);
  return Number.isFinite(id) ? id : null;
};

const parseOutfitName = (outfit) => {
  if (typeof outfit === 'string') return normalizeOutfitName(outfit);
  return normalizeOutfitName(outfit?.name ?? outfit?.outfitName ?? '');
};

export const getNsfwKeywords = () => cachedKeywords;
export const getNsfwExactTags = () => cachedExact;
export const getHiddenOutfitIds = () => cachedHiddenOutfitIds;
export const getHiddenOutfitNames = () => cachedHiddenOutfitNames;
export const getHiddenScenarioTagIds = () => cachedHiddenScenarioTagIds;
export const getHiddenScenarioTags = () => cachedHiddenScenarioTags;

export const loadNsfwKeywords = async () => {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const response = await fetch(`${CDN_URL}/data/nsfw_keywords.json`);
      if (!response.ok) throw new Error('nsfw_keywords fetch failed');
      const data = await response.json();
      if (Array.isArray(data?.keywords) && data.keywords.length > 0) {
        cachedKeywords = tagList(data.keywords);
      }
      if (Array.isArray(data?.exact) && data.exact.length > 0) {
        cachedExact = tagList(data.exact);
      }
      if (Array.isArray(data?.hidden_outfit_ids) && data.hidden_outfit_ids.length > 0) {
        cachedHiddenOutfitIds = outfitIdList(data.hidden_outfit_ids);
      }
      if (Array.isArray(data?.hidden_outfit_names) && data.hidden_outfit_names.length > 0) {
        cachedHiddenOutfitNames = data.hidden_outfit_names.map(normalizeOutfitName).filter(Boolean);
      }
      if (Array.isArray(data?.hidden_scenario_tag_ids) && data.hidden_scenario_tag_ids.length > 0) {
        cachedHiddenScenarioTagIds = data.hidden_scenario_tag_ids
          .map((id) => Number.parseInt(String(id), 10))
          .filter((id) => Number.isFinite(id));
      }
      if (Array.isArray(data?.hidden_scenario_tags) && data.hidden_scenario_tags.length > 0) {
        cachedHiddenScenarioTags = data.hidden_scenario_tags.map(normalizeOutfitName).filter(Boolean);
      }
    } catch (error) {
      console.warn('[nsfwTags] keywords fetch failed', error);
      loadPromise = null;
    }
    return {
      keywords: cachedKeywords,
      exact: cachedExact,
      hidden_outfit_ids: cachedHiddenOutfitIds,
      hidden_outfit_names: cachedHiddenOutfitNames,
      hidden_scenario_tag_ids: cachedHiddenScenarioTagIds,
      hidden_scenario_tags: cachedHiddenScenarioTags
    };
  })();
  return loadPromise;
};

const spacedHasBoundedStem = (normalizedToken, stem) => {
  const spaced = ` ${String(normalizedToken || '').replace(/_/g, ' ')} `;
  return new RegExp(`[^a-z0-9]${stem}[^a-z0-9]`).test(spaced);
};

const keywordMatchesToken = (normalizedToken, keyword) => {
  if (BOUNDED_NSFW_STEMS.has(keyword)) return spacedHasBoundedStem(normalizedToken, keyword);
  if (normalizedToken.includes(keyword)) return true;
  if (!REVERSE_NSFW_KEYWORDS.has(keyword)) return false;
  return normalizedToken.length >= 6 && keyword.includes(normalizedToken);
};

const tokenMatchesNsfw = (normalizedToken, keywords, exact) => {
  if (!normalizedToken) return false;
  if (exact.includes(normalizedToken)) return true;
  if (spacedHasBoundedStem(normalizedToken, 'anus')) return true;
  return keywords.some((keyword) => keywordMatchesToken(normalizedToken, keyword));
};

export const collectNsfwPromptTokens = (promptText, options = {}) => {
  const keywords = Array.isArray(options.keywords) ? options.keywords : cachedKeywords;
  const exact = Array.isArray(options.exact) ? options.exact : cachedExact;
  const rawTokens = String(promptText || '')
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (rawTokens.length === 0) {
    const haystack = normalizeTag(promptText);
    if (!haystack) return [];
    const matches = [];
    exact.forEach((tag) => {
      if (haystack === tag) matches.push(tag);
    });
    keywords.forEach((keyword) => {
      if (keywordMatchesToken(haystack, keyword)) matches.push(keyword);
    });
    if (spacedHasBoundedStem(haystack, 'anus')) matches.push('anus');
    return [...new Set(matches)];
  }

  const seen = new Set();
  const matches = [];
  rawTokens.forEach((raw) => {
    if (!tokenMatchesNsfw(normalizeTag(raw), keywords, exact)) return;
    const key = normalizeTag(raw);
    if (seen.has(key)) return;
    seen.add(key);
    matches.push(raw);
  });
  return matches;
};

export const promptContainsNsfw = (promptText, options = {}) =>
  collectNsfwPromptTokens(promptText, options).length > 0;

export const characterIsNsfw = (character) => {
  if (!character || typeof character !== 'object') return false;
  if (character.isN === 1 || character.isN === true || character.isN === '1') return true;
  if (character.is_nsfw === true || character.is_nsfw === 1 || character.is_nsfw === '1') return true;
  if (character.nsfw === true || character.nsfw === 1) return true;
  return false;
};

export const outfitMatchesHiddenDenylist = (outfit) => {
  if (outfit?.sfw_hidden === true || outfit?.sfw_hidden === 1 || outfit?.sfw_hidden === '1') return true;
  const id = parseOutfitId(outfit);
  if (id != null && cachedHiddenOutfitIds.includes(id)) return true;
  const name = parseOutfitName(outfit);
  return Boolean(name) && cachedHiddenOutfitNames.includes(name);
};

export const outfitIsHiddenInFilteredMode = (outfit) => {
  if (getNsfwEnabled()) return false;
  return outfitMatchesHiddenDenylist(outfit);
};

export const filterOutfitsForSfw = (outfits) => {
  if (getNsfwEnabled() || !Array.isArray(outfits)) return outfits;
  return outfits.filter((outfit) => !outfitMatchesHiddenDenylist(outfit));
};

export const filterOutfitIdsForSfw = (outfitIds) => {
  if (!Array.isArray(outfitIds) && !(outfitIds instanceof Set)) return outfitIds;
  const list = Array.isArray(outfitIds) ? outfitIds : Array.from(outfitIds);
  if (getNsfwEnabled()) return Array.isArray(outfitIds) ? outfitIds : new Set(list);
  const kept = list.filter((id) => !outfitMatchesHiddenDenylist({ id }));
  return Array.isArray(outfitIds) ? kept : new Set(kept);
};

const scenarioTagMatchesDenylist = (tag) => {
  if (tag == null) return false;
  if (typeof tag === 'number' || (typeof tag === 'string' && /^\d+$/.test(tag.trim()))) {
    const id = Number.parseInt(String(tag), 10);
    if (Number.isFinite(id) && cachedHiddenScenarioTagIds.includes(id)) return true;
  }
  if (typeof tag === 'string') {
    const name = normalizeOutfitName(tag);
    return Boolean(name) && cachedHiddenScenarioTags.includes(name);
  }
  if (typeof tag === 'object') {
    const id = Number.parseInt(String(tag.id ?? tag.tag_id ?? ''), 10);
    if (Number.isFinite(id) && cachedHiddenScenarioTagIds.includes(id)) return true;
    const name = normalizeOutfitName(tag.slug || tag.label || tag.name || tag.text || '');
    return Boolean(name) && cachedHiddenScenarioTags.includes(name);
  }
  return false;
};

export const scenarioTagsAreNsfw = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return false;
  return tags.some((tag) => scenarioTagMatchesDenylist(tag));
};

export const scenarioIsNsfw = (scenario) => {
  if (!scenario || typeof scenario !== 'object') return false;
  if (scenario.isN === 1 || scenario.isN === true || scenario.isN === '1') return true;
  if (scenario.is_nsfw === true || scenario.is_nsfw === 1 || scenario.is_nsfw === '1') return true;
  if (scenarioTagsAreNsfw(scenario.tags)) return true;
  if (promptContainsNsfw(scenario.banner_prompt || scenario.bannerPrompt || '')) return true;
  return false;
};

export const filterScenariosForSfw = (scenarios) => {
  if (getNsfwEnabled() || !Array.isArray(scenarios)) return scenarios;
  return scenarios.filter((scenario) => !scenarioIsNsfw(scenario));
};

export const filterScenarioTagOptionsForSfw = (tagOptions) => {
  if (getNsfwEnabled() || !Array.isArray(tagOptions)) return tagOptions;
  return tagOptions.filter((tag) => !scenarioTagMatchesDenylist(tag));
};

const scenarioTagDisplayName = (tag) => {
  if (tag == null) return '';
  if (typeof tag === 'string' || typeof tag === 'number') return String(tag).trim();
  if (typeof tag === 'object') {
    return String(tag.slug || tag.label || tag.name || tag.text || tag.id || tag.tag_id || '').trim();
  }
  return String(tag).trim();
};

export const collectNsfwScenarioTags = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return [];
  const seen = new Set();
  const matches = [];
  tags.forEach((tag) => {
    if (!scenarioTagMatchesDenylist(tag)) return;
    const name = scenarioTagDisplayName(tag);
    if (!name) return;
    const key = normalizeOutfitName(name) || name;
    if (seen.has(key)) return;
    seen.add(key);
    matches.push(name);
  });
  return matches;
};

export const getBlockedNsfwScenarioTagMessage = (tags) => {
  if (getNsfwEnabled()) return null;
  const blocked = collectNsfwScenarioTags(tags);
  if (blocked.length === 0) return null;
  return getFilteredNsfwPromptMessage(blocked);
};

const locationFlagIsNsfw = (value) => (
  value === true || value === 1 || value === '1'
);

export const locationIsNsfw = (location) => {
  if (location == null) return false;
  if (typeof location === 'string') {
    return promptContainsNsfw(location);
  }
  if (typeof location !== 'object') return false;
  if (locationFlagIsNsfw(location.isN) || locationFlagIsNsfw(location.is_nsfw)) return true;
  const name = location.name ?? location.location_name ?? location.label ?? '';
  const description = location.description ?? location.prompt ?? location.descr ?? '';
  return promptContainsNsfw(name) || promptContainsNsfw(description);
};

export const filterLocationsForSfw = (locations) => {
  if (getNsfwEnabled() || locations == null) return locations;
  if (Array.isArray(locations)) {
    return locations.filter((location) => !locationIsNsfw(location));
  }
  if (typeof locations !== 'object') return locations;
  const filtered = {};
  for (const [id, value] of Object.entries(locations)) {
    if (locationIsNsfw(value)) continue;
    filtered[id] = value;
  }
  return filtered;
};

export const stripNsfwTokens = (promptText) => String(promptText || '')
  .split(/[,\n]/)
  .map((token) => token.trim())
  .filter((token) => token && !promptContainsNsfw(token))
  .join(', ');

const FILTERED_NSFW_GENERIC_FALLBACK =
  'NSFW tags are not allowed in Filtered mode. Switch to Unrestricted or remove adult tags.';
const FILTERED_NSFW_TAGS_FALLBACK =
  'NSFW tags are not allowed in Filtered mode: {tags}. Switch to Unrestricted or remove adult tags.';

export const getFilteredNsfwPromptMessage = (tags) => {
  const list = Array.isArray(tags) ? tags.filter(Boolean) : [];
  if (list.length === 0) {
    return resolveCreateCommonText('errors.nsfw_tags_filtered_mode_generic')
      || FILTERED_NSFW_GENERIC_FALLBACK;
  }
  const translated = resolveCreateCommonText('errors.nsfw_tags_filtered_mode', { tags: list.join(', ') });
  if (translated) return translated;
  return FILTERED_NSFW_TAGS_FALLBACK.replaceAll('{tags}', list.join(', '));
};

export const getBlockedNsfwPromptMessage = (promptText) => {
  if (getNsfwEnabled()) return null;
  const blocked = collectNsfwPromptTokens(promptText);
  if (blocked.length === 0) return null;
  return getFilteredNsfwPromptMessage(blocked);
};

const FILTERED_UNRECOGNIZED_FALLBACK =
  'Filtered mode only allows recognized booru tags. Please remove: {tags}';
const FILTERED_UNRECOGNIZED_TITLE_FALLBACK =
  'Filtered mode only allows recognized booru tags.';

export const getFilteredUnrecognizedPromptMessage = (tags) => {
  const list = Array.isArray(tags) ? tags.filter(Boolean) : [];
  const translated = resolveCreateCommonText('errors.unrecognized_tags_filtered_mode', { tags: list.join(', ') });
  if (translated) return translated;
  return FILTERED_UNRECOGNIZED_FALLBACK.replaceAll('{tags}', list.join(', '));
};

export const getFilteredUnrecognizedTagTitle = () => (
  resolveCreateCommonText('errors.unrecognized_tag_filtered_title')
  || FILTERED_UNRECOGNIZED_TITLE_FALLBACK
);

export const getBlockedNsfwScenarioMessage = (scenario) => {
  if (getNsfwEnabled()) return null;
  if (!scenarioIsNsfw(scenario)) return null;
  const tagNames = collectNsfwScenarioTags(scenario?.tags);
  const promptTokens = collectNsfwPromptTokens(scenario?.banner_prompt || scenario?.bannerPrompt || '');
  const seen = new Set();
  const blocked = [];
  [...tagNames, ...promptTokens].forEach((item) => {
    const key = normalizeOutfitName(item) || String(item).toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    blocked.push(item);
  });
  return getFilteredNsfwPromptMessage(blocked);
};

export const applySfwQueryParam = (searchParams) => {
  if (!getNsfwEnabled()) searchParams.set('sfw', '1');
  return searchParams;
};

export const withSfwQueryString = (url) => {
  if (getNsfwEnabled() || !url) return url;
  return url.includes('?') ? `${url}&sfw=1` : `${url}?sfw=1`;
};

void loadNsfwKeywords();
